import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Navbar,
  Nav,
  Container,
  Row,
  Col,
  Button,
  Card,
} from 'react-bootstrap';
import '../../styles/custom-fixes.css';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import IconButton from '@mui/material/IconButton';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Typography from '@mui/material/Typography';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import NavDropdown from '../../components/NavDropdown';
import LogoSvg from '../../assets/logo.svg';
import Footer from './Footer';
import './VideoTutorial.css';

const productItems = [
  { label: 'Audits', href: '/#audits' },
  { label: 'Corrective Actions', href: '/#corrective-actions' },
  { label: 'Digital Forms & QR', href: '/#digital-forms' },
  { label: 'Policy & Procedure Builder', href: '/#policy-builder' },
  { label: 'Photo Inspector', href: '/#photo-inspector' },
  { label: 'Document Assistant', href: '/#document-assistant' },
  { label: 'Registers/Assets', href: '/#registers-assets' },
];

const industriesItems = [
  { label: 'Construction', href: '/#construction' },
  { label: 'Childcare', href: '/#childcare' },
  { label: 'Health', href: '/#health' },
  { label: 'NFP', href: '/#nfp' },
  { label: 'Local Government', href: '/#local-government' },
];

function VideoTutorial() {
  const { mode, toggleTheme } = useThemeMode();
  const [videos, setVideos] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedVideo, setSelectedVideo] = React.useState(null);

  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  // Extract YouTube video ID from different URL formats
  const extractYoutubeId = (url) => {
    if (!url) return '';
    try {
      const parsed = new URL(url);
      if (parsed.hostname === 'youtu.be') {
        return parsed.pathname.replace('/', '');
      }
      const vParam = parsed.searchParams.get('v');
      if (vParam) return vParam;
      const segments = parsed.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] || '';
    } catch (e) {
      console.error('Invalid YouTube URL:', url, e);
      return '';
    }
  };

  // Fetch video tutorials from backend on mount
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await dataAPI.getVideoTutorials({ showLoading: false });
        if (response.status === 'success' && Array.isArray(response.data)) {
          const mapped = response.data.map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            youtubeUrl: item.video_link,
            videoId: extractYoutubeId(item.video_link),
            duration: '', // API does not provide duration yet
            category: 'Tutorial',
            createdAt: item.created_at,
          }));
          setVideos(mapped);
        } else {
          setError('Failed to load video tutorials.');
        }
      } catch (err) {
        console.error('Error fetching video tutorials:', err);
        setError('Failed to load video tutorials.');
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  useEffect(() => {
    if (selectedVideo) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedVideo]);

  useEffect(() => {
    if (!selectedVideo) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') closeVideo();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [selectedVideo]);

  const getThumbnailUrl = (videoId) =>
    `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;

  const openVideo = (e, video) => {
    e.preventDefault();
    setSelectedVideo(video);
  };

  const closeVideo = () => setSelectedVideo(null);

  return (
    <div
      style={{
        backgroundColor: mode === 'dark' ? '#111' : '#fff',
        minHeight: '100vh',
      }}
    >
      {/* Header – same as Home */}
      <Navbar
        expand="lg"
        className={`header-bar py-2 ${mode === 'dark' ? 'bg-dark navbar-dark' : 'bg-white navbar-light'}`}
        style={mode === 'light' ? { backgroundColor: '#fff' } : {}}
      >
        <Container fluid className="px-3">
          <Navbar.Brand as={Link} to="/" className="header-logo">
            <img
              src={LogoSvg}
              alt="Complimate Logo"
              style={{ height: 50, verticalAlign: 'middle' }}
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="mainNavbar" />
          <Navbar.Collapse id="mainNavbar">
            <Nav
              className="mx-auto mb-2 mb-lg-0 header-nav"
              style={{
                gap: 18,
                fontSize: 18,
                fontWeight: 200,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <NavDropdown label="Product" items={productItems} href="/#product" />
              <NavDropdown label="Industries" items={industriesItems} href="/#industries" />
              <Nav.Link href="/#pricing">Pricing</Nav.Link>
              <Nav.Link as={Link} to="/learning-hub" className="learning-hub-active">
                Learning Hub
              </Nav.Link>
              <Nav.Link href="/#about">About</Nav.Link>
              <Nav.Link as={Link} to="/contact-us">
                Contact
              </Nav.Link>
            </Nav>
            <IconButton
              onClick={toggleTheme}
              color="inherit"
              sx={{
                ml: 1,
                backgroundColor: mode === 'dark' ? '#222' : '#e0f7f7',
                borderRadius: '999px',
                px: 2.5,
                py: 1,
                transition: 'background 0.2s',
                '&:hover': {
                  backgroundColor: mode === 'dark' ? '#333' : '#b2f5ea',
                },
                boxShadow:
                  mode === 'dark'
                    ? '0 2px 8px rgba(0,0,0,0.15)'
                    : '0 2px 8px rgba(38,195,192,0.10)',
              }}
            >
              <Brightness4Icon />
              <Typography variant="caption" sx={{ ml: 0.5 }}>
                {mode === 'light' ? 'Dark' : 'Light'}
              </Typography>
            </IconButton>
            <Button
              as={Link}
              to="/login"
              variant="outline-primary"
              className="btn-outline-teal ms-lg-3 header-login"
            >
              Log In
            </Button>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Video Tutorials Section */}
      <section
        className={`video-tutorial-section ${mode === 'dark' ? 'video-tutorial-dark' : ''}`}
      >
        <Container className="py-5">
          <div className="video-tutorial-header text-center mb-5">
            <h1 className="video-tutorial-title">Learning Hub</h1>
            <p className="video-tutorial-subtitle">
              Video tutorials to get the most out of Complimate. From setup to audits, policies, and reports.
            </p>
          </div>

          {loading && (
            <p className="video-tutorial-status text-center">Loading tutorials...</p>
          )}

          {error && !loading && (
            <p className="video-tutorial-status video-tutorial-status-error text-center">
              {error}
            </p>
          )}

          {!loading && !error && videos.length === 0 && (
            <p className="video-tutorial-status text-center">
              No video tutorials available yet.
            </p>
          )}

          {!loading && !error && videos.length > 0 && (
            <Row className="g-4">
              {videos.map((video) => (
                <Col key={video.id} xs={12} sm={6} lg={4}>
                  <Card className="video-tutorial-card h-100">
                    <button
                      type="button"
                      className="video-tutorial-thumb-link"
                      onClick={(e) => openVideo(e, video)}
                      aria-label={`Play ${video.title}`}
                    >
                      <div className="video-tutorial-thumb-wrap">
                        <img
                          src={getThumbnailUrl(video.videoId)}
                          alt={video.title}
                          className="video-tutorial-thumb"
                        />
                        <div className="video-tutorial-play-overlay">
                          <PlayCircleOutlineIcon className="play-icon" />
                        </div>
                        {video.videoId && (
                          <span className="video-tutorial-duration">
                            {video.duration || 'Watch'}
                          </span>
                        )}
                      </div>
                    </button>
                    <Card.Body className="video-tutorial-card-body">
                      <Card.Title className="video-tutorial-card-title">
                        {video.title}
                      </Card.Title>
                      <Card.Text className="video-tutorial-card-desc">
                        {video.description}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </section>

      {/* Video player modal – plays on site */}
      {selectedVideo && (
        <div
          className="video-tutorial-modal-overlay"
          onClick={closeVideo}
          role="dialog"
          aria-modal="true"
          aria-label="Video player"
        >
          <div
            className="video-tutorial-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="video-tutorial-modal-close"
              onClick={closeVideo}
              aria-label="Close video"
            >
              ×
            </button>
            <div className="video-tutorial-modal-title">{selectedVideo.title}</div>
            <div className="video-tutorial-modal-embed-wrap">
              <iframe
                title={selectedVideo.title}
                src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="video-tutorial-modal-iframe"
              />
            </div>
            <a
              href={selectedVideo.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="video-tutorial-modal-youtube-link"
            >
              Open in YouTube
            </a>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default VideoTutorial;
